import { ScrollView, Pressable, View } from "react-native";
import { EventListSkeleton } from "@/components/Skeleton";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useEvents } from "@/hooks/useEvents";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return dateStr; }
}

const Screen = styled.ScrollView`flex: 1; background-color: ${colors.bg};`;
const Header = styled.View`flex-direction: row; justify-content: space-between; align-items: center; padding: 20px 20px 12px;`;
const HeaderLabel = styled.Text`font-size: 12px; font-weight: ${font.extrabold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px;`;
const NewBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.md}px; padding: 10px 16px;`;
const NewBtnText = styled.Text`color: #fff; font-size: 12px; font-weight: ${font.bold};`;

const Card = styled.View`
  margin: 0 20px 12px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.06;
  shadow-radius: 8px;
  elevation: 2;
`;

const CardTitle = styled.Text`font-size: 16px; font-weight: ${font.extrabold}; color: ${colors.ink}; letter-spacing: -0.3px;`;
const CardMeta = styled.Text`font-size: 13px; color: ${colors.inkSecondary}; font-weight: ${font.medium}; margin-top: 4px;`;

const BadgeRow = styled.View`flex-direction: row; gap: 6px; margin-top: 10px;`;
const Badge = styled.View<{ bg: string; border: string }>`
  padding: 3px 10px; border-radius: 999px;
  background-color: ${(p) => p.bg}; border: 1px solid ${(p) => p.border};
`;
const BadgeText = styled.Text<{ color: string }>`font-size: 11px; font-weight: ${font.bold}; color: ${(p) => p.color};`;

const CountsRow = styled.View`flex-direction: row; gap: 12px; margin-top: 10px;`;
const CountText = styled.Text`font-size: 12px; font-weight: ${font.bold}; color: ${colors.inkMuted};`;

const SectionLabel = styled.Text`font-size: 12px; font-weight: ${font.extrabold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; padding: 20px 20px 8px;`;

const EmptyCard = styled.View`margin: 0 20px; background-color: ${colors.white}; border-radius: ${radius.lg}px; border: 1px solid ${colors.line}; padding: 32px; align-items: center;`;
const EmptyText = styled.Text`font-size: 14px; color: ${colors.inkMuted}; text-align: center;`;
const EmptyEmoji = styled.Text`font-size: 36px; margin-bottom: 12px;`;

const LoadingContainer = styled.View`flex: 1; background-color: ${colors.bg}; justify-content: center; align-items: center;`;
const Spacer = styled.View`height: 32px;`;

export default function EventsScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { upcoming, past, loading } = useEvents(club?.id);

  const canCreate = membership?.role === "admin" || isAnyCaptain(user?.uid);

  if (loading) return <View style={{ flex: 1, backgroundColor: colors.bg }}><EventListSkeleton /></View>;

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Header>
          <HeaderLabel>Events</HeaderLabel>
          {canCreate && (
            <Pressable onPress={() => router.push("/(app)/create-event")} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
              <NewBtn><NewBtnText>+ New event</NewBtnText></NewBtn>
            </Pressable>
          )}
        </Header>

        {upcoming.length === 0 && past.length === 0 && (
          <EmptyCard>
            <EmptyEmoji>📅</EmptyEmoji>
            <EmptyText>No events yet.{canCreate ? " Create one!" : ""}</EmptyText>
          </EmptyCard>
        )}

        {upcoming.length > 0 && (
          <>
            <SectionLabel>Upcoming</SectionLabel>
            {upcoming.map((evt) => (
              <Pressable key={evt.id} onPress={() => router.push(`/(app)/event-detail?id=${evt.id}`)} style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}>
                <Card>
                  <CardTitle>{evt.title}</CardTitle>
                  <CardMeta>{formatDate(evt.date)} · {evt.time} · {evt.location}</CardMeta>
                  <BadgeRow>
                    <Badge bg={evt.type === "open" ? colors.brand50 : colors.amber50} border={evt.type === "open" ? colors.brandLight : colors.amber200}>
                      <BadgeText color={evt.type === "open" ? colors.brandDark : colors.amber}>{evt.type === "open" ? "Open" : "Invite only"}</BadgeText>
                    </Badge>
                    {evt.isPaid && (
                      <Badge bg={colors.amber50} border={colors.amber200}>
                        <BadgeText color={colors.amber}>£{(evt.fee / 100).toFixed(0)}</BadgeText>
                      </Badge>
                    )}
                  </BadgeRow>
                  <CountsRow>
                    <CountText>✅ {evt.goingCount} going</CountText>
                    <CountText>🤔 {evt.maybeCount} maybe</CountText>
                  </CountsRow>
                </Card>
              </Pressable>
            ))}
          </>
        )}

        {past.length > 0 && (
          <>
            <SectionLabel>Past</SectionLabel>
            {past.map((evt) => (
              <Pressable key={evt.id} onPress={() => router.push(`/(app)/event-detail?id=${evt.id}`)} style={({ pressed }) => ({ opacity: pressed ? 0.5 : 0.6 })}>
                <Card>
                  <CardTitle>{evt.title}</CardTitle>
                  <CardMeta>{formatDate(evt.date)} · {evt.location}</CardMeta>
                  <CountsRow>
                    <CountText>✅ {evt.goingCount} went</CountText>
                  </CountsRow>
                </Card>
              </Pressable>
            ))}
          </>
        )}

        <Spacer />
      </ContentWidth>
    </Screen>
  );
}
